import { useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { DeleteOutline, Edit } from "@mui/icons-material";
import { Chip, IconButton, Switch } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Cancel";
import { GridCheckCircleIcon } from "@mui/x-data-grid";

import { TableWrapperStyle } from "../style";
import { getComparator } from "../helpers";

import { Data } from "./types";
import headCells from "./tableHeadCells";

import { Order } from "@/utils/table-utils";
import ConfirmationModal from "@/components/app/modalVariants/confirmation";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { useDeleteUser, useUpdateUserActivation } from "@/api/hooks/useUsers";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { USER_ROLE } from "@/constants/enums/user";
import { useUser } from "@/context/authContext";
import EnhancedTableHead from "@/components/app/EnhancedTableHeader";
import TableSkeleton from "@/components/app/TableSkeleton";
import Loading from "@/components/app/Loading/tableLoading";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";

export default function GenreTable({
    data: rows,
    totalItems,
    rowsPerPage,
    page,
    onChange,
    isFetching,
    isLoading,
}: {
    data: Data[],
    totalItems: number,
    page: number,
    rowsPerPage: number,
    isFetching: boolean,
    isLoading: boolean,
    // eslint-disable-next-line no-unused-vars
    onChange: (data: { page?: number, rowsPerPage?: number }) => void,
}) {
    const navigate = useNavigate();
    const user = useUser();
    const editAccess = useCheckPermissionAccess(PagesAccess.USER, OperationAccess.UPDATE);
    const deleteAccess = useCheckPermissionAccess(PagesAccess.USER, OperationAccess.DELETE);

    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<keyof Data>("updatedAt");
    const [confirmModalId, setConfirmModal] = useState<number | null>(null);

    const { mutate: updateActivation } = useUpdateUserActivation();
    const { mutate: deleteUser, isPending } = useDeleteUser();

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        onChange({ page: newPage, rowsPerPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
    };

    const handleActivation = (id: number, active: boolean) => {
        updateActivation({ id: Number(id), body: { active: !active } });
    };

    const handleEdit = (id: number) => navigate(ROUTE_CONSTANTS.USER_MANAGEMENT.USERS.UPDATE.EDIT_BY_ID(id).ABSOLUTE);
    const handleClose = () => setConfirmModal(null);
    const handleDelete = () =>
        confirmModalId &&
        deleteUser(confirmModalId, {
            onSuccess: () => {
                setConfirmModal(null);
            },
        });

    const visibleRows = useMemo(() => [...rows].sort(getComparator(order, orderBy)), [order, orderBy, rows]);

    return (
        <TableWrapperStyle className="table-style-wrapper" sx={{ width: "100%", marginTop: "2em" }}>
            <TableContainer sx={{ maxHeight: "700px" }}>
                <Table
                    stickyHeader
                    aria-labelledby="tableTitle"
                    className="table"
                    component="div"
                    size="medium"
                    sx={{ padding: ".5em" }}
                >
                    <EnhancedTableHead<Data>
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        rowCount={rows.length}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody component="div">
                        {isLoading && <TableSkeleton cells={headCells.map((item) => item.id)} />}
                        {!isLoading && !isFetching && visibleRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={headCells.length}>No Data To Show</TableCell>
                            </TableRow>
                        )}
                        {isFetching && <Loading />}
                        {visibleRows.map((row) => {
                            const isSuperAdmin = row.role === USER_ROLE.SUPERADMIN;

                            return (
                                <TableRow key={row.id} className="table-row" component="div" role="checkbox" tabIndex={-1}>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.id}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.firstName}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.lastName}</span>
                                    </TableCell>

                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value fit">
                                            <Chip
                                                color={row.emailVerified ? "success" : "error"}
                                                icon={row.emailVerified ? <GridCheckCircleIcon /> : <CancelIcon />}
                                                label={row.email}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value" style={{ padding: "13px" }}>
                                            <Chip color="default" label={row.role} size="small" variant="outlined" />
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.updatedAt}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value fit">
                                            {isSuperAdmin && (
                                                <Chip
                                                    color={row.active ? "success" : "error"}
                                                    icon={<GridCheckCircleIcon />}
                                                    label={row.active ? "Active" : "Not Active"}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                            {!isSuperAdmin && (
                                                <Switch
                                                    checked={row.active}
                                                    color="success"
                                                    disabled={!editAccess}
                                                    inputProps={{ "aria-label": "controlled" }}
                                                    size="small"
                                                    onChange={() => handleActivation(row.id, row.active)}
                                                />
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value" style={{ padding: "5px" }}>
                                            <IconButton
                                                disabled={user?.id !== row.id && !editAccess}
                                                onClick={() => handleEdit(row.id)}
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton disabled={!deleteAccess} onClick={() => setConfirmModal(row.id)}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <ConfirmationModal
                description="Are you sure to delete this Item ?"
                handleClose={handleClose}
                loading={isPending}
                open={Boolean(confirmModalId)}
                onConfirm={handleDelete}
            />
        </TableWrapperStyle>
    );
}
